import { useEffect, useState, useCallback, type ReactNode, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface ExceptionRow {
  readonly exceptionId: string;
  readonly exceptionTypeCode: string;
  readonly severityCode: string;
  readonly status: string;
  readonly description: string;
  readonly reviewId: string | null;
  readonly jobNumber: string | null;
  readonly resolutionRemarks: string | null;
  readonly createdAt: string;
  readonly resolvedAt: string | null;
}

export function ShExceptionRegisterPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const [rows, setRows] = useState<ExceptionRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Resolve form
  const [resolveId, setResolveId] = useState<string | null>(null);
  const [resolveRemarks, setResolveRemarks] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await apiRequest<ExceptionRow[]>('/v1/sh/exceptions', { token });
      setRows(r); setError(null);
    } catch (e) { setError((e as Error).message); }
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  const resolving = resolveId ? rows.find((r) => r.exceptionId === resolveId) ?? null : null;

  const handleResolve = useCallback(async (e: FormEvent) => {
    e.preventDefault();
    if (!resolveId || busy) return;
    if (resolveRemarks.length < 10) { setError(t('sh.exc.remarksMin')); return; }
    setBusy(true); setError(null); setInfo(null);
    try {
      await apiRequest(`/v1/sh/exceptions/${encodeURIComponent(resolveId)}/resolve`, {
        method: 'POST', token, body: { resolutionRemarks: resolveRemarks },
      });
      setInfo(t('sh.exc.resolvedMsg'));
      setResolveId(null); setResolveRemarks('');
      await load();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }, [resolveId, resolveRemarks, token, busy, load]);

  return (
    <main aria-label="sh-exception-register">
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
        <Link to="/sh/dashboard" data-testid="back-dashboard">{t('sh.common.dashboard')}</Link>
        <h1 style={{ fontSize: 20 }}>{t('sh.exc.title')}</h1>
      </div>

      {error && <p role="alert" style={{ color: 'var(--color-danger)', marginBottom: 12 }}>{error}</p>}
      {info && <p role="status" style={{ color: 'var(--color-success)', marginBottom: 12 }}>{info}</p>}

      {/* ── Resolve inline form ──────────────────────── */}
      {resolving && (
        <section style={{
          padding: 12, borderRadius: 8, marginBottom: 12,
          border: '1px solid var(--color-border-default)',
          background: 'var(--color-bg-elevated)',
        }} aria-label="resolve-exception-form">
          <h3 style={{ fontSize: 14, marginBottom: 8 }}>{t('sh.exc.resolveHeading', { type: resolving.exceptionTypeCode, job: resolving.jobNumber ?? '—' })}</h3>
          <form onSubmit={handleResolve} aria-label="resolve-form">
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label htmlFor="ex-remarks" style={{ fontSize: 11 }}>{t('sh.exc.resolutionRemarks')}</label>
                <textarea id="ex-remarks" rows={2} value={resolveRemarks} onChange={(e) => setResolveRemarks(e.target.value)}
                          data-testid="input-resolve-remarks"
                          style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid var(--color-border-default)', resize: 'vertical' }} />
              </div>
              <Button type="submit" variant="primary" disabled={busy} data-testid="btn-resolve">{t('sh.common.resolve')}</Button>
              <Button type="button" variant="danger" onClick={() => setResolveId(null)}>{t('sh.common.cancel')}</Button>
            </div>
          </form>
        </section>
      )}

      {rows.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)' }}>{t('sh.exc.empty')}</p>
      ) : (
        <DataTable<ExceptionRow>
          ariaLabel="exception-table"
          rows={rows}
          rowKey={(r) => r.exceptionId}
          columns={[
            { key: 'type', header: t('sh.common.type'), render: (r) => r.exceptionTypeCode },
            { key: 'severity', header: t('sh.common.severity'), render: (r) => <span style={{ color: r.severityCode === 'CRITICAL' ? 'var(--color-danger)' : r.severityCode === 'HIGH' ? 'var(--color-warning)' : 'inherit' }}>{r.severityCode}</span> },
            { key: 'status', header: t('sh.common.status'), render: (r) => r.status },
            { key: 'description', header: t('sh.common.description'), render: (r) => <span style={{ display: 'inline-block', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.description}</span> },
            { key: 'review', header: t('sh.common.reviewCol'), render: (r) => (r.reviewId ? <Link to={`/sh/review/${r.reviewId}`}>{r.jobNumber ?? '—'}</Link> : '—') },
            { key: 'raised', header: t('sh.common.raised'), render: (r) => <span style={{ fontSize: 12 }}>{at(r.createdAt)}</span> },
            { key: 'resolved', header: t('sh.common.resolved'), render: (r) => <span style={{ fontSize: 12 }}>{r.resolvedAt ? at(r.resolvedAt) : '—'}</span> },
            {
              key: 'actions', header: '',
              render: (r) => (
                r.status === 'OPEN' ? (
                  <Button type="button" variant="secondary" onClick={() => { setResolveId(r.exceptionId); setResolveRemarks(''); }}
                          data-testid={`btn-resolve-${r.exceptionId}`}>
                    {t('sh.common.resolve')}
                  </Button>
                ) : null
              ),
            },
          ]}
        />
      )}
    </main>
  );
}
